<template>
    <div slider question :id="`question-${questionData.number}`">
        <questionheader :questionData="questionData"></questionheader>
        <div data-question-main>
            <div
                v-for="(sq, index) in questionData.subQuestions"
                :key="`sq-${index}`"
                :data-answer="questionData.type"
                :data-outerfield="hasOuterFields"
            >
                <div data-sq :id="`question-${questionData.number}_sq-${sq.number}`">
                    <template v-for="(answer, index) in sq.answers">
                        <div
                            v-if="answer.type === `Digit` && questionData.type === `Slider`"
                            data-slider-container
                            :disabled="isDisabled(sq)"
                            :key="index"
                        >
                            <div data-slider-numbers aria-hidden="true">
                                <div
                                    data-number
                                    :id="`question-${questionData.number}_sq-${sq.number}_min-value`"
                                >{{ minValue }}</div>
                                <div
                                    data-number
                                    :id="`question-${questionData.number}_sq-${sq.number}_max-value`"
                                >{{restrictions.maxValue}}</div>
                            </div>
                            <div data-range-container :data-locked="!answer.isChecked">
                                <output
                                    v-if="questionData.displayValue"
                                    :style="bubblePositioning(answer)"
                                    :data-is-sliding="rangeBeingUsedId === answer.id"
                                    :id="`question-${questionData.number}_sq-${sq.number}_output`"
                                >{{ answer.openField }}</output>
                                <label
                                    data-only-screenreader
                                >{{get.textResource(`rs_slider`)}} {{get.textResource(`rs_min`)}} {{ minValue }} {{get.textResource(`rs_max`)}} {{ restrictions.maxValue }}</label>
                                <input
                                    type="range"
                                    :id="`question-${questionData.number}_sq-${sq.number}_slider-${answer.number}`"
                                    :min="minValue"
                                    :required="restrictions.required"
                                    :max="restrictions.maxValue"
                                    v-model="answer.openField"
                                    @mousedown="rangeMouseDown(answer)"
                                    @touchstart="rangeMouseDown(answer)"
                                    @keydown.left="rangeMouseDown(answer)"
                                    @keydown.right="rangeMouseDown(answer)"
                                    @mouseup="rangeMouseUp"
                                    @touchend="rangeMouseDown"
                                    :disabled="isDisabled(sq)"
                                />
                            </div>
                            <div
                                v-if="answer.labelLeft !== `` && answer.labelRight !== ``"
                                data-slider-labels
                            >
                                <template v-if="sqLeftAnswer(sq).label !== ``">
                                    <label
                                        data-only-screenreader
                                    >{{get.textResource(`rs_description_left`)}}</label>
                                    <label
                                        :id="`question-${questionData.number}_sq-${sq.number}_descr-left`"
                                        v-html="sqLeftAnswer(sq).label"
                                        data-left
                                    ></label>
                                </template>
                                <template v-if="sqRightAnswer(sq).label !== ``">
                                    <label
                                        data-only-screenreader
                                    >{{get.textResource(`rs_description_right`)}}</label>
                                    <label
                                        :id="`question-${questionData.number}_sq-${sq.number}_descr-right`"
                                        v-html="sqRightAnswer(sq).label"
                                        data-right
                                    ></label>
                                </template>
                            </div>
                        </div>
                        <div
                            v-else-if="answer.type === `OuterField` && questionData.type !== `SemanticDifferential`"
                            :key="`answer-${answer.id}`"
                            :disabled="!get.editingAllowed(questionData)"
                        >
                            <checkboxradio
                                :questionData="questionData"
                                :outerfield="true"
                                :answerData="answer"
                                :sqData="sq"
                                :showLabel="true"
                                type="checkbox"
                                @element-click="outerfieldClicked(sq)"
                                :disabled="!get.editingAllowed(questionData)"
                            />
                        </div>
                        <div
                            v-else-if="questionData.type === `SemanticDifferential` && answer.type !== `OuterField`"
                            data-semdiff
                            :key="`question-${questionData.number}_sq-${sq.number}_answer-${answer.id}`"
                            :disabled="isDisabled(sq)"
                            :style="{
                                width: `calc(100% / ${sqAnswers(sq).length})`   
                            }"
                        >
                            <checkboxradio
                                :questionData="questionData"
                                :type="`radio`"
                                :name="`option-${sq.id}`"
                                :answerData="answer"
                                :sqData="sq"
                                @element-click="semdiffChanged(answer, sq)"
                                :disabled="isDisabled(sq)"
                            />
                        </div>
                    </template>
                </div>
                <div data-labels v-if="questionData.type === `SemanticDifferential`">
                    <div data-left-answer>
                        <label v-html="sq.answers[0].label"></label>
                    </div>
                    <div data-right-answer>
                        <label
                            v-html="sqOuterfield(sq) ? sq.answers[sq.answers.length -2].label : sq.answers[sq.answers.length -1].label"
                        ></label>
                    </div>
                </div>
                <div
                    v-if="questionData.type === `SemanticDifferential` && sqOuterfield(sq)"
                    data-outerfield-container
                >
                    <checkboxradio
                        :questionData="questionData"
                        :outerfield="true"
                        :answerData="sqOuterfield(sq)"
                        :showLabel="true"
                        :sqData="sq"
                        type="checkbox"
                        @element-click="outerfieldClicked(sq)"
                        :disabled="!get.editingAllowed(sq, false)"
                    />
                </div>
            </div>
        </div>
        <questionfooter :questionData="questionData" />
        <validationpopup :questionValidation="questionValidation" />
    </div>
</template>

<script>
import mixins from "@/global/mixins";

export default {
    name: `slidersemdiff-question`,
    mixins: [mixins.defaultQuestion],
    data: function () {
        return {
            rangeBeingUsedId: -1, // Used to determine which bulb should be shown when the user drags a slider
        };
    },
    computed: {
        answersGiven: function(){
            return this.questionData.subQuestions.filter((sq) => {
                //Either the slider or outerfield has to be checked for a subquestion to be answered
                return sq.answers.filter((answer) => answer.isChecked).length > 0;
            }).length;
        },
        // If there are outerfields the styling of the page should be slightly different
        hasOuterFields: function () {
            return this.questionData.subQuestions[0].answers.filter((answer) => answer.type === `OuterField`).length > 0;
        },
        // When a minimal value is not specified -1 will be returned from the back-end but it should be 0
        minValue: function () {
            return this.restrictions.minValue === -1 ? 0 : this.restrictions.minValue;
        },
    },
    methods: {
        outerfieldClicked: function (sq) {
            let outerfield = this.sqOuterfield(sq);

            outerfield.isChecked = !outerfield.isChecked;
        },
        // Calculate the position of the bubble(That thing that shows the value)
        bubblePositioning: function (answer) {
            let min = this.minValue;
            let max = this.restrictions.maxValue;
            let left = Number(((answer.openField - min) * 100) / (max - min));
            let translate = -55 * (left / 100);

            return {
                left: `${left}%`,
                transform: `translate(${translate}px, 0)`,
            };
        },
        // Should the question be disabled?
        isDisabled: function (sq) {
            return !this.get.editingAllowed(sq) || this.state.global.showLoader;
        },
        rangeMouseDown: function (answer) {
            this.rangeBeingUsedId = answer.id;
            answer.isChecked = true;
        },
        rangeMouseUp: function () {
            this.rangeBeingUsedId = -1;
        },
        // Sliders have three answers. This holds the value of the slider and the label and description of the left side
        sqLeftAnswer: function (sq) {
            return sq.answers.find((a) => a.type === `Digit`);
        },
        // Sliders have three answers. This holds the value of the slider and the label and description of the right side
        sqRightAnswer: function (sq) {
            return sq.answers.find((a) => a.type === `None`);
        },
        sqAnswers: function (sq) {
            return sq.answers.filter((answer) => answer.type === `Radiobutton`);
        },
        // Sliders have three answers. This returns the outerfield
        sqOuterfield: function (sq) {
            return sq.answers.find((a) => a.type === `OuterField`);
        },
        semdiffChanged: function (answer, sq) {
            sq.answers.forEach((answer) => {
                answer.isChecked = false;
            });

            answer.isChecked = true;
        },
    },
};
</script>

<style lang="scss" scoped>
@import "./../styles/variables.scss";

@mixin slidertrack {
    width: 100%;
    cursor: pointer;
    background: #ebebeb;
    border-radius: 25px;
    height: 12px;
    border: 1px solid #878787;
    transition: 0.5s;
}

@mixin slidertumb {
    border-radius: 7px;
    background: var(--primary-color);
    cursor: pointer;
    -webkit-appearance: none;
    border-radius: 20px;
    width: 55px;
    height: 35px;
    border-width: 0;
    margin-top: -12px;
    transition: 0.4s;
}

@mixin slidertrackfocus {
    background: #d8d8d8;
    box-shadow: 0 0 2px #d8d8d8;
}

[data-answer="Slider"] {
    margin: 30px 0;
    flex-direction: column;
    align-items: flex-start;

    [data-sq] {
        flex-direction: column;
    }
}

[data-sq] {
    display: flex;
    justify-content: space-evenly;
    width: 100%;
}

[data-outerfield] {
    margin-top: 0;
}

input[type="range"] {
    cursor: pointer;
    -webkit-appearance: none;
    color: #ebebeb;
    width: 100%;
    background: transparent;

    &:invalid {
        background: red !important;
    }

    &::-webkit-slider-runnable-track {
        @include slidertrack;
    }

    &::-moz-range-track {
        @include slidertrack;
    }

    &::-webkit-slider-thumb {
        @include slidertumb;
    }
    &::-moz-range-thumb {
        @include slidertumb;
    }

    &:focus::-moz-range-track {
        @include slidertrackfocus;
    }

    &:focus::-webkit-slider-runnable-track {
        @include slidertrackfocus;
    }

    &:focus {
        outline: none;
    }

    &:hover::-webkit-slider-runnable-track {
        background: #c7c7c7;
    }
    &:hover:-moz-range-track {
        background: #c7c7c7;
    }
}

[data-range-container] {
    position: relative;
    display: flex;
    height: 70px;
    align-items: center;
    width: 100%;
    transition: 0.5s;

    &[data-locked="true"] {
        output {
            visibility: hidden;
        }

        input[type="range"]::-webkit-slider-thumb {
            width: 40px;
            filter: brightness(0.65);
        }

        input[type="range"]::-moz-range-thumb {
            width: 40px;
            filter: brightness(0.65);
        }
    }
}

[data-answer="SemanticDifferential"] {
    display: flex;
    flex-direction: column;

    ~ [data-answer="SemanticDifferential"] {
        margin-top: 60px;
    }
}

[data-slider-numbers] {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
}

[data-semdiff-labels] {
    display: flex;
    justify-content: space-between;
    width: 100%;
    flex-direction: row;
    font-size: calc(var(--AnswerStyling-font-size) * 1px);
    font-family: var(--AnswerStyling-font-family-public-name);
    color: var(--AnswerStyling-color);

    label {
        max-width: 200px;
    }
}

[data-outerfield-container] {
    align-self: flex-start;
}

[data-number] {
    border: 2px solid #454848;
    border-radius: 4px;
    padding: 5px 8px;
    background: #fff;
    font-size: 13px;
    pointer-events: none;
    font-weight: bold;
}

[data-slider-labels] {
    display: flex;
    justify-content: space-between;
}

[data-semdiff] {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-direction: column;
}

[data-labels] {
    display: flex;
    width: 100%;
    justify-content: space-between;
    padding: 0 25px 0 25px;

    label {
        overflow-wrap: break-word;
        word-break: break-word;
    }

    [data-left-answer] {
        display: flex;
        width: 50%;
        justify-content: flex-start;
        max-width: 200px;
        padding-left: 20px;
        text-align: left;
    }

    [data-right-answer] {
        display: flex;
        justify-content: flex-end;
        width: 50%;
        max-width: 200px;
        padding-right: 20px;
        text-align: right;
    }
}

[disabled] {
    opacity: 0.5;
    pointer-events: none;
    [data-number] {
        border-color: #e6e6e6;
        background: #f8f8f8;
        color: #a7a7a7;
        cursor: default;
        pointer-events: none;
    }
}

[data-checkboxradio] {
    margin: 20px 0;
}

[data-slider-container] {
    width: 100%;

    label {
        width: 40%;

        &[data-left] {
            text-align: left;
        }

        &[data-right] {
            text-align: right;
        }
    }

    output {
        color: white;
        border-radius: 4px;
        display: inline-block;
        padding: 6px 10px;
        left: 46.5%;
        width: 55px;
        height: 27px;
        font-weight: bold;
        position: absolute;
        text-align: center;
        pointer-events: none;
        font-size: 14px;
    }
}

@include media-break(tablet) {
    [data-labels] {
        [data-left-answer] {
            padding-left: 0px;
        }

        [data-right-answer] {
            padding-right: 0px;
        }
    }
}

@include media-break(mobile) {
    [data-slider-container] output {
        &[data-is-sliding] {
            background: var(--primary-color);
            animation: bubble 0.5s forwards;

            &:after {
                content: "";
                width: 0;
                height: 0;
                border-left: 10px solid transparent;
                border-right: 10px solid transparent;
                border-top: 10px solid var(--primary-color);
                bottom: -10px;
                position: absolute;
                left: 18px;
            }
        }
    }

    [data-labels] {
        padding: 0 5px 0 5px;
    }
}

@keyframes bubble {
    0% {
        opacity: 0;
        top: 0;
    }

    100% {
        opacity: 1;
        top: -30px;
    }
}
</style>